/**
 * 🐾 Fissal Stage Asset & Anti-Metal Tag Registry
 * Mappings between ferrite-backed NTAG215 coin tags and Guardian Productions stage hardware.
 */

export interface StageAsset {
  id: string;
  tagUri: string;
  altUids?: string[];
  name: string;
  category: 'lighting' | 'audio' | 'dimmer' | 'power' | 'sfx';
  channel?: number;
  specs: string;
  watts: number;
  voltage: number;
  lamp?: string;
  connector: string;
  recommendedCircuit: string;
  notes: string;
}

export const GUARDIAN_RIG_ASSETS: StageAsset[] = [
  {
    id: 'altman-360q-01',
    tagUri: 'fissal://fixture/altman-360q-01',
    altUids: ['04:a2:3f:1a:8b:2c:80', '04:11:22:33:44:55:01'],
    name: 'Altman Leko 360Q #1',
    category: 'lighting',
    channel: 1,
    specs: '500W Ellipsoidal Spot • 40° Barrel • Drop-in Iris Slot',
    watts: 500,
    voltage: 120,
    lamp: 'EHG 500W 120V Tungsten-Halogen (G9.5 base)',
    connector: '2P&G Stage Pin (Bates)',
    recommendedCircuit: 'Distro Dimmer Pack A (Ch 1)',
    notes: 'Soft-start curve required to tame 12x cold filament inrush.'
  },
  {
    id: 'altman-360q-02',
    tagUri: 'fissal://fixture/altman-360q-02',
    altUids: ['04:a2:3f:1a:8b:2c:81', '04:11:22:33:44:55:02'],
    name: 'Altman Leko 360Q #2',
    category: 'lighting',
    channel: 2,
    specs: '500W Ellipsoidal Spot • 40° Barrel',
    watts: 500,
    voltage: 120,
    lamp: 'EHG 500W 120V Tungsten-Halogen',
    connector: '2P&G Stage Pin (Bates)',
    recommendedCircuit: 'Distro Dimmer Pack A (Ch 2)',
    notes: 'Key light position left stage.'
  },
  {
    id: 'altman-360q-03',
    tagUri: 'fissal://fixture/altman-360q-03',
    altUids: ['04:a2:3f:1a:8b:2c:82', '04:11:22:33:44:55:03'],
    name: 'Altman Leko 360Q #3',
    category: 'lighting',
    channel: 3,
    specs: '500W Ellipsoidal Spot • 30° Barrel (Narrow Throw)',
    watts: 500,
    voltage: 120,
    lamp: 'EHG 500W 120V Tungsten-Halogen',
    connector: '2P&G Stage Pin (Bates)',
    recommendedCircuit: 'Distro Dimmer Pack B (Ch 1)',
    notes: 'Center solo spotlight.'
  },
  {
    id: 'altman-360q-04',
    tagUri: 'fissal://fixture/altman-360q-04',
    altUids: ['04:a2:3f:1a:8b:2c:83', '04:11:22:33:44:55:04'],
    name: 'Altman Leko 360Q #4',
    category: 'lighting',
    channel: 4,
    specs: '500W Ellipsoidal Spot • 40° Barrel',
    watts: 500,
    voltage: 120,
    lamp: 'EHG 500W 120V Tungsten-Halogen',
    connector: '2P&G Stage Pin (Bates)',
    recommendedCircuit: 'Distro Dimmer Pack B (Ch 2)',
    notes: 'Key light position right stage.'
  },
  {
    id: 'par64-bank',
    tagUri: 'fissal://fixture/par64-bank',
    altUids: ['04:a2:3f:1a:8b:2c:84'],
    name: 'Par 64 Narrow Spots (Pair)',
    category: 'lighting',
    channel: 5,
    specs: '2x 500W Sealed Beam Spot Cans',
    watts: 1000,
    voltage: 120,
    lamp: 'Q500PAR64/NSP',
    connector: '2P&G Stage Pin',
    recommendedCircuit: 'Branch 20A Circuit (Isolated)',
    notes: 'High inrush load. Never switch hot on 15A branch.'
  },
  {
    id: 'dmx-rgb-wash',
    tagUri: 'fissal://fixture/rgb-wash-bank',
    altUids: ['04:a2:3f:1a:8b:2c:85'],
    name: 'DMX RGB Wash Bank (4 Units)',
    category: 'lighting',
    channel: 7,
    specs: '4x 120W High-Output LED Flood Cans (DMX 4-Channel)',
    watts: 480,
    voltage: 120,
    connector: 'PowerCON In/Thru + 3-Pin DMX XLR',
    recommendedCircuit: 'LED Distro Circuit',
    notes: 'Channels 7-10: Red, Green, Blue, Strobe/Macro.'
  },
  {
    id: 'nsi-5000-dimmer',
    tagUri: 'fissal://dimmer/nsi-5000',
    altUids: ['04:a2:3f:1a:8b:2c:86'],
    name: 'NSI 5000 4-Channel Dimmer Pack',
    category: 'dimmer',
    channel: 1,
    specs: '4x 1200W Channels • Microplex & DMX512 • 20A Max Input',
    watts: 2400,
    voltage: 120,
    connector: 'NEMA 5-20P Edison / 2P&G Outputs',
    recommendedCircuit: 'Dedicated 20A Breaker (16A Continuous Max)',
    notes: 'DIP Switch set to DMX Address 001. Pack output curve: Incandescent/Linear.'
  },
  {
    id: 'behringer-x32',
    tagUri: 'fissal://console/behringer-x32',
    altUids: ['04:a2:3f:1a:8b:2c:87'],
    name: 'Behringer X32 Digital Console',
    category: 'audio',
    specs: '40-Input, 25-Bus Digital Mixing Console • OSC UDP :10023',
    watts: 120,
    voltage: 120,
    connector: 'EtherCON RJ45 Remote Remote Jack',
    recommendedCircuit: 'FOH Clean Audio Power',
    notes: 'Configured static IP: 192.168.1.1. Requires /xremote heartbeat every 6 seconds.'
  }
];

/**
 * Resolves a scanned NFC tag payload (URI, text, or hardware UID) to a certified StageAsset
 */
export function resolveStageAsset(tagData: string): StageAsset | null {
  if (!tagData) return null;
  const clean = tagData.trim().toLowerCase();

  for (const asset of GUARDIAN_RIG_ASSETS) {
    if (asset.tagUri.toLowerCase() === clean) return asset;
    if (clean.includes(asset.id)) return asset;
    if (asset.altUids && asset.altUids.some(uid => uid.toLowerCase() === clean)) {
      return asset;
    }
  }

  // Fallback: match by channel number if "channel X" or "ch X"
  const chMatch = clean.match(/(?:channel|ch)\s*([0-9]+)/i);
  if (chMatch) {
    const ch = parseInt(chMatch[1], 10);
    const found = GUARDIAN_RIG_ASSETS.find(a => a.channel === ch);
    if (found) return found;
  }

  return null;
}
